import { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import PhotoGrid from '../components/shared/PhotoGrid';
import FullScreenView from '../components/shared/FullScreenView';
import { authHeaders } from '../utils/auth';
import { apiFetch } from '../utils/api';
import { formatExpiry } from '../utils/formatTime';
import type { Photo, PhotoLike, ApiErrorBody } from '../types';

// ─── Types ──────────────────────────────────────────────────────────────────

type Tab = 'my-photos' | 'group-photos' | 'gallery' | 'review-gallery';

type GroupPhoto = PhotoLike & { batchId: number };

type GroupBatch = {
    batchId: number;
    galleryName: string;
    season: string;
    photos: GroupPhoto[];
};

type ReleasedBatch = {
    batchId: number;
    galleryName: string;
    season: string;
    photos: (PhotoLike & { renamedFilename?: string | null })[];
};

type GalleryPhoto = PhotoLike & { requested: boolean };

type WindowStatus = {
    active: boolean;
    windowEndsAt: string | null;
    secondaryBatchId: number | null;
};

type LoadState<T> =
    | { status: 'loading' }
    | { status: 'error'; message: string }
    | { status: 'ready'; data: T };

// ─── Component ──────────────────────────────────────────────────────────────

export default function ArtistPortfolio() {
    const navigate  = useNavigate();
    const location  = useLocation();

    const [searchParams] = useSearchParams();
    const [activeTab, setActiveTab] = useState<Tab>(() => {
        const fromQuery = searchParams.get('tab') as Tab | null;
        const fromState = (location.state as { tab?: Tab } | null)?.tab;
        return fromQuery ?? fromState ?? 'my-photos';
    });

    // Sync tab when search params change (e.g. drawer navigation)
    useEffect(() => {
        const fromQuery = searchParams.get('tab') as Tab | null;
        if (fromQuery) setActiveTab(fromQuery);
    }, [searchParams]);

    // My Photos
    const [myPhotos, setMyPhotos] = useState<LoadState<Photo[]>>({ status: 'loading' });
    const [myActiveIndex, setMyActiveIndex]       = useState<number | null>(null);
    const [downloadingId, setDownloadingId]       = useState<number | null>(null);

    // Group Photos
    const [groupState, setGroupState] = useState<LoadState<GroupBatch[]>>({ status: 'loading' });
    const [groupActiveIndex, setGroupActiveIndex] = useState<number | null>(null);
    const [groupDownloadId, setGroupDownloadId]   = useState<number | null>(null);

    // Review Gallery
    const [windowStatus, setWindowStatus]         = useState<WindowStatus | null>(null);
    const [galleryState, setGalleryState]         = useState<LoadState<GalleryPhoto[]> | null>(null);
    const [requestedIds, setRequestedIds]         = useState<Set<number>>(new Set());
    const [confirmMsg, setConfirmMsg] = useState('');
    const [galleryActiveIndex, setGalleryActiveIndex] = useState<number | null>(null);

    // Released gallery
    const [releasedState, setReleasedState] = useState<LoadState<ReleasedBatch[]> | null>(null);
    const [releasedActiveIndex, setReleasedActiveIndex] = useState<number | null>(null);
    const [releasedDownloadId, setReleasedDownloadId] = useState<number | null>(null);

    useEffect(() => {
        if (!localStorage.getItem('token')) navigate('/login', { replace: true });
    }, [navigate]);

    // Fetch My Photos
    useEffect(() => {
        let cancelled = false;
        apiFetch('/api/photos/portfolio', { headers: authHeaders() })
            .then(async (res) => {
                if (!res.ok) {
                    const body: ApiErrorBody = await res.json().catch(() => ({}));
                    throw new Error(body.message ?? `Failed (${res.status})`);
                }
                return res.json();
            })
            .then((body: { data?: Photo[] }) => {
                if (!cancelled) setMyPhotos({ status: 'ready', data: body.data ?? [] });
            })
            .catch((err) => {
                if (!cancelled) setMyPhotos({ status: 'error', message: err instanceof Error ? err.message : String(err) });
            });
        return () => { cancelled = true; };
    }, []);

    // Fetch Group Photos
    useEffect(() => {
        let cancelled = false;
        apiFetch('/api/photos/group', { headers: authHeaders() })
            .then(async (res) => {
                if (!res.ok) {
                    const body: ApiErrorBody = await res.json().catch(() => ({}));
                    throw new Error(body.message ?? `Failed (${res.status})`);
                }
                return res.json();
            })
            .then((body: { data?: GroupBatch[] }) => {
                if (!cancelled) setGroupState({ status: 'ready', data: body.data ?? [] });
            })
            .catch((err) => {
                if (!cancelled) setGroupState({ status: 'error', message: err instanceof Error ? err.message : String(err) });
            });
        return () => { cancelled = true; };
    }, []);

    // Fetch window status
    useEffect(() => {
        apiFetch('/api/gallery/window-status', { headers: authHeaders() })
            .then(r => r.ok ? r.json() : null)
            .then((data: WindowStatus | null) => { if (data) setWindowStatus(data); })
            .catch(() => setWindowStatus({ active: false, windowEndsAt: null, secondaryBatchId: null }));
    }, []);

    // Fetch released gallery photos when tab first activated
    useEffect(() => {
        if (activeTab !== 'gallery' || releasedState !== null) return;
        setReleasedState({ status: 'loading' });
        apiFetch('/api/gallery/released', { headers: authHeaders() })
            .then(async (res) => {
                if (!res.ok) {
                    const body: ApiErrorBody = await res.json().catch(() => ({}));
                    throw new Error(body.message ?? `Failed (${res.status})`);
                }
                return res.json();
            })
            .then((body: { data: ReleasedBatch[] }) => {
                const batches = body.data ?? [];
                setReleasedState({ status: 'ready', data: batches });
                // Mark all released gallery IDs as seen so the notification badge clears
                try {
                    const ids = batches.map(b => b.batchId);
                    const existing: number[] = JSON.parse(
                        localStorage.getItem('prosceno_gallery_seen_ids') ?? '[]'
                    );
                    const merged = Array.from(new Set([...existing, ...ids]));
                    localStorage.setItem('prosceno_gallery_seen_ids', JSON.stringify(merged));
                } catch { /* localStorage unavailable — badge persists */ }
            })
            .catch((err) => {
                setReleasedState({ status: 'error', message: err instanceof Error ? err.message : String(err) });
            });
    }, [activeTab, releasedState]);

    // Fetch review gallery photos when tab first activated
    useEffect(() => {
        if (activeTab !== 'review-gallery' || galleryState !== null) return;
        setGalleryState({ status: 'loading' });
        apiFetch('/api/gallery/non-selected', { headers: authHeaders() })
            .then(async (res) => {
                if (!res.ok) {
                    const body: ApiErrorBody = await res.json().catch(() => ({}));
                    throw new Error(body.message ?? `Failed (${res.status})`);
                }
                return res.json();
            })
            .then((body: { photos: GalleryPhoto[] }) => {
                setGalleryState({ status: 'ready', data: body.photos });
                setRequestedIds(new Set(body.photos.filter(p => p.requested).map(p => p.id)));
            })
            .catch((err) => {
                setGalleryState({ status: 'error', message: err instanceof Error ? err.message : String(err) });
            });
    }, [activeTab, galleryState]);

    // ─── Handlers ───────────────────────────────────────────────────────────

    const handleDownload = useCallback(async (photoId: number, filename: string | null | undefined) => {
        setDownloadingId(photoId);
        try {
            const res = await apiFetch(`/api/photos/portfolio/${photoId}/download`, { headers: authHeaders() });
            if (!res.ok) throw new Error(`Download failed (${res.status})`);
            const blob = await res.blob();
            const url  = URL.createObjectURL(blob);
            const a    = document.createElement('a');
            a.href = url; a.download = filename || 'photo';
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
        } catch (err) {
            alert(`Could not download photo. ${err instanceof Error ? err.message : String(err)}`);
        } finally { setDownloadingId(null); }
    }, []);

    const handleGroupDownload = useCallback(async (photoId: number, filename: string | null | undefined) => {
        setGroupDownloadId(photoId);
        try {
            const res = await apiFetch(`/api/photos/group/${photoId}/download`, { headers: authHeaders() });
            if (!res.ok) throw new Error(`Download failed (${res.status})`);
            const blob = await res.blob();
            const url  = URL.createObjectURL(blob);
            const a    = document.createElement('a');
            a.href = url; a.download = filename || 'photo';
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
        } catch (err) {
            alert(`Could not download photo. ${err instanceof Error ? err.message : String(err)}`);
        } finally { setGroupDownloadId(null); }
    }, []);

    const handleReleasedDownload = useCallback(async (photoId: number, filename: string | null | undefined) => {
        setReleasedDownloadId(photoId);
        try {
            const res = await apiFetch(`/api/photos/released/${photoId}/download`, { headers: authHeaders() });
            if (!res.ok) throw new Error(`Download failed (${res.status})`);
            const blob = await res.blob();
            const url  = URL.createObjectURL(blob);
            const a    = document.createElement('a');
            a.href = url; a.download = filename || 'photo';
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
        } catch (err) {
            alert(`Could not download photo. ${err instanceof Error ? err.message : String(err)}`);
        } finally { setReleasedDownloadId(null); }
    }, []);

    const toggleRequest = useCallback(async (photoId: number, newRequested: boolean) => {
        setRequestedIds(prev => {
            const next = new Set(prev);
            newRequested ? next.add(photoId) : next.delete(photoId);
            return next;
        });
        try {
            if (newRequested) {
                const res = await apiFetch('/api/gallery/review-requests', {
                    method: 'POST',
                    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
                    body: JSON.stringify({ photoId }),
                });
                if (!res.ok && res.status !== 200) throw new Error();
            } else {
                const res = await apiFetch(`/api/gallery/review-requests/${photoId}`, {
                    method: 'DELETE', headers: authHeaders(),
                });
                if (!res.ok) throw new Error();
            }
        } catch {
            setRequestedIds(prev => {
                const next = new Set(prev);
                newRequested ? next.delete(photoId) : next.add(photoId);
                return next;
            });
        }
    }, []);

    const handleSubmitRequests = () => {
        setActiveTab('my-photos');
        setConfirmMsg('Your review requests have been submitted.');
        setTimeout(() => setConfirmMsg(''), 5000);
    };

    // ─── Derived ────────────────────────────────────────────────────────────

    const allGroupPhotos: GroupPhoto[] =
        groupState.status === 'ready' ? groupState.data.flatMap(b => b.photos) : [];

    const showReviewTab = windowStatus?.active === true;

    const allReleasedPhotos =
        releasedState?.status === 'ready' ? releasedState.data.flatMap(b => b.photos) : [];

    // ─── Tab renderers ──────────────────────────────────────────────────────

    function renderMyPhotos() {
        if (myPhotos.status === 'loading') return <p className='ap-status-msg'>Loading your photos…</p>;
        if (myPhotos.status === 'error')   return <p className='ap-status-msg ap-status-error'>{myPhotos.message}</p>;
        if (myPhotos.data.length === 0)    return <p className='ap-status-msg'>No approved photos yet.</p>;

        const activePhoto = myActiveIndex !== null ? myPhotos.data[myActiveIndex] : null;
        return (
            <>
            {confirmMsg && (
                <div className='ap-confirm-msg'>✓ {confirmMsg}</div>
            )}
                <PhotoGrid
                    photos={myPhotos.data}
                    onSelect={setMyActiveIndex}
                    columns={4}
                    renderOverlay={(photo) => (
                        <button
                            className='ap-download-btn'
                            disabled={downloadingId === photo.id}
                            title='Download original'
                            onClick={() => handleDownload(photo.id, photo.renamedFilename || photo.originalFilename || null)}
                        >
                            {downloadingId === photo.id ? '…' : '⬇'}
                        </button>
                    )}
                />
                <FullScreenView
                    photo={activePhoto}
                    onClose={() => setMyActiveIndex(null)}
                    onPrev={() => setMyActiveIndex(i => Math.max(0, (i ?? 0) - 1))}
                    onNext={() => setMyActiveIndex(i => Math.min(myPhotos.data.length - 1, (i ?? 0) + 1))}
                    hasPrev={(myActiveIndex ?? 0) > 0}
                    hasNext={myActiveIndex !== null && myActiveIndex < myPhotos.data.length - 1}
                    actionArea={activePhoto && (
                        <button
                            className='ap-fs-btn'
                            onClick={() => handleDownload(activePhoto.id, activePhoto.renamedFilename || activePhoto.originalFilename || null)}
                            disabled={downloadingId === activePhoto.id}
                        >
                            {downloadingId === activePhoto.id ? 'Downloading…' : 'Download original'}
                        </button>
                    )}
                />
            </>
        );
    }

    function renderGroupPhotos() {
        if (groupState.status === 'loading') return <p className='ap-status-msg'>Loading group photos…</p>;
        if (groupState.status === 'error')   return <p className='ap-status-msg ap-status-error'>{groupState.message}</p>;
        if (groupState.data.length === 0)    return <p className='ap-status-msg'>No group photos available yet.</p>;

        const flatIndexMap = new Map(allGroupPhotos.map((p, i) => [p.id, i]));
        const activePhoto  = groupActiveIndex !== null ? allGroupPhotos[groupActiveIndex] : null;

        return (
            <>
                {groupState.data.map((batch) => (
                    <div key={batch.batchId} className='ap-group-section'>
                        <div className='ap-group-section-header'>
                            <span className='ap-group-section-title'>{batch.galleryName}</span>
                            <span className='ap-group-section-season'>{batch.season}</span>
                        </div>
                        <PhotoGrid
                            photos={batch.photos}
                            onSelect={(index) => {
                                setGroupActiveIndex(flatIndexMap.get(batch.photos[index].id) ?? 0);
                            }}
                            columns={4}
                            renderOverlay={(photo) => (
                                <button
                                    className='ap-download-btn'
                                    disabled={groupDownloadId === photo.id}
                                    title='Download original'
                                    onClick={() => handleGroupDownload(photo.id, photo.renamedFilename || photo.originalFilename || null)}
                                >
                                    {groupDownloadId === photo.id ? '…' : '⬇'}
                                </button>
                            )}
                        />
                    </div>
                ))}
                <FullScreenView
                    photo={activePhoto ?? null}
                    onClose={() => setGroupActiveIndex(null)}
                    onPrev={() => setGroupActiveIndex(i => Math.max(0, (i ?? 0) - 1))}
                    onNext={() => setGroupActiveIndex(i => Math.min(allGroupPhotos.length - 1, (i ?? 0) + 1))}
                    hasPrev={(groupActiveIndex ?? 0) > 0}
                    hasNext={groupActiveIndex !== null && groupActiveIndex < allGroupPhotos.length - 1}
                    actionArea={activePhoto && (
                        <button
                            className='ap-fs-btn'
                            onClick={() => handleGroupDownload(activePhoto.id, activePhoto.renamedFilename || activePhoto.originalFilename || null)}
                            disabled={groupDownloadId === activePhoto.id}
                        >
                            {groupDownloadId === activePhoto.id ? 'Downloading…' : 'Download original'}
                        </button>
                    )}
                />
            </>
        );
    }

    function renderReviewGallery() {
        if (windowStatus === null) return <p className='ap-status-msg'>Loading…</p>;

        if (!windowStatus.active) {
            return (
                <div className='ap-closed-card'>
                    <h3 className='ap-closed-title'>No Active Review Window</h3>
                    <p className='ap-closed-body'>
                        When admin opens a secondary review window, non-selected photos will appear
                        here so you can request that Artistic Staff take another look.
                    </p>
                </div>
            );
        }

        if (!galleryState || galleryState.status === 'loading') {
            return <p className='ap-status-msg'>Loading…</p>;
        }
        if (galleryState.status === 'error') {
            return <p className='ap-status-msg ap-status-error'>{galleryState.message}</p>;
        }

        const photos      = galleryState.data;
        const activePhoto = galleryActiveIndex !== null ? photos[galleryActiveIndex] : null;

        return (
            <>
                <div className='ap-rg-bar'>
                    <p className='ap-rg-hint'>Flag non-selected photos you'd like Artistic Staff to reconsider.</p>
                    <div className='ap-rg-stats'>
                        {windowStatus.windowEndsAt && (
                            <p className='ap-rg-expires'>
                                Expires {formatExpiry(windowStatus.windowEndsAt)}
                            </p>
                        )}
                        <div className='ap-rg-count'>
                            <span className='ap-rg-count-num'>{requestedIds.size}</span>
                            <span className='ap-rg-count-label'>{requestedIds.size === 1 ? 'photo requested' : 'photos requested'}</span>
                        </div>
                    </div>
                </div>

                {photos.length === 0 ? (
                    <p className='ap-status-msg'>No non-selected photos available in this window.</p>
                ) : (
                    <PhotoGrid
                        photos={photos}
                        onSelect={setGalleryActiveIndex}
                        columns={4}
                        renderOverlay={(photo) => (
                            <div
                                className={`ap-rg-toggle${requestedIds.has(photo.id) ? ' requested' : ''}`}
                                onClick={() => toggleRequest(photo.id, !requestedIds.has(photo.id))}
                                title={requestedIds.has(photo.id) ? 'Cancel request' : 'Request review'}
                            >
                                {requestedIds.has(photo.id) ? '✓' : '+'}
                            </div>
                        )}
                    />
                )}

                {photos.length > 0 && (
                    <div className='ap-rg-submit'>
                        <button className='ap-rg-submit-btn' onClick={handleSubmitRequests}>
                            Submit Requests
                        </button>
                        <p className='ap-rg-submit-hint'>Your selections are saved automatically. Submit when you're done.</p>
                    </div>
                )}

                <FullScreenView
                    photo={activePhoto ?? null}
                    onClose={() => setGalleryActiveIndex(null)}
                    onPrev={() => setGalleryActiveIndex(i => Math.max(0, (i ?? 0) - 1))}
                    onNext={() => setGalleryActiveIndex(i => Math.min(photos.length - 1, (i ?? 0) + 1))}
                    hasPrev={(galleryActiveIndex ?? 0) > 0}
                    hasNext={galleryActiveIndex !== null && galleryActiveIndex < photos.length - 1}
                    actionArea={activePhoto && (
                        <button
                            className={`ap-rg-fs-btn${requestedIds.has(activePhoto.id) ? ' requested' : ''}`}
                            onClick={() => toggleRequest(activePhoto.id, !requestedIds.has(activePhoto.id))}
                        >
                            {requestedIds.has(activePhoto.id) ? 'Cancel Request' : 'Request Review'}
                        </button>
                    )}
                />
            </>
        );
    }

    function renderReleasedGallery() {
        if (!releasedState || releasedState.status === 'loading') return <p className='ap-status-msg'>Loading gallery…</p>;
        if (releasedState.status === 'error') return <p className='ap-status-msg ap-status-error'>{releasedState.message}</p>;
        if (releasedState.data.length === 0) return <p className='ap-status-msg'>No galleries have been released to your group yet.</p>;

        const activePhoto = releasedActiveIndex !== null ? allReleasedPhotos[releasedActiveIndex] : null;
        return (
            <>
                {releasedState.data.map((batch) => (
                    <div key={batch.batchId} className='ap-released-section'>
                        <div className='ap-released-section-header'>
                            <span className='ap-released-section-title'>{batch.galleryName}</span>
                            <span className='ap-released-section-season'>{batch.season}</span>
                        </div>
                        <PhotoGrid
                            photos={batch.photos}
                            onSelect={(localIdx) => {
                                const offset = allReleasedPhotos.indexOf(batch.photos[localIdx]);
                                setReleasedActiveIndex(offset >= 0 ? offset : null);
                            }}
                            columns={4}
                            renderOverlay={(photo) => (
                                <button
                                    className='ap-download-btn'
                                    disabled={releasedDownloadId === photo.id}
                                    title='Download original'
                                    onClick={() => handleReleasedDownload(photo.id, (photo as { renamedFilename?: string | null }).renamedFilename || photo.originalFilename || null)}
                                >
                                    {releasedDownloadId === photo.id ? '…' : '⬇'}
                                </button>
                            )}
                        />
                    </div>
                ))}
                <FullScreenView
                    photo={activePhoto ?? null}
                    onClose={() => setReleasedActiveIndex(null)}
                    onPrev={() => setReleasedActiveIndex(i => Math.max(0, (i ?? 0) - 1))}
                    onNext={() => setReleasedActiveIndex(i => Math.min(allReleasedPhotos.length - 1, (i ?? 0) + 1))}
                    hasPrev={(releasedActiveIndex ?? 0) > 0}
                    hasNext={releasedActiveIndex !== null && releasedActiveIndex < allReleasedPhotos.length - 1}
                    actionArea={activePhoto && (
                        <button
                            className='ap-fs-btn'
                            onClick={() => handleReleasedDownload(activePhoto.id, (activePhoto as { renamedFilename?: string | null }).renamedFilename || activePhoto.originalFilename || null)}
                            disabled={releasedDownloadId === activePhoto.id}
                        >
                            {releasedDownloadId === activePhoto.id ? 'Downloading…' : 'Download original'}
                        </button>
                    )}
                />
            </>
        );
    }

    // ─── Render ─────────────────────────────────────────────────────────────

    const switchTab = (tab: Tab) => { setActiveTab(tab); };

    return (
        <div className='ap-page'>
            {/* Desktop tab bar */}
            <div className='ap-tabs'>
                <button className={`ap-tab${activeTab === 'my-photos' ? ' active' : ''}`} onClick={() => switchTab('my-photos')}>
                    My Photos
                </button>
                <button className={`ap-tab${activeTab === 'group-photos' ? ' active' : ''}`} onClick={() => switchTab('group-photos')}>
                    Group Photos
                </button>
                <button className={`ap-tab${activeTab === 'gallery' ? ' active' : ''}`} onClick={() => switchTab('gallery')}>
                    Galleries
                </button>
                {showReviewTab && (
                    <button className={`ap-tab${activeTab === 'review-gallery' ? ' active' : ''}`} onClick={() => switchTab('review-gallery')}>
                        Secondary Review
                    </button>
                )}
            </div>



            <div className='ap-tab-content'>
                {activeTab === 'my-photos'      && renderMyPhotos()}
                {activeTab === 'group-photos'   && renderGroupPhotos()}
                {activeTab === 'gallery'        && renderReleasedGallery()}
                {activeTab === 'review-gallery' && renderReviewGallery()}
            </div>
        </div>
    );
}